import { sql } from "drizzle-orm";
import {
  pgTable,
  pgEnum,
  text,
  varchar,
  uuid,
  timestamp,
  numeric,
  integer,
  date,
  boolean,
  jsonb,
  check,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================================================
// COMPLIANCE MANAGER — REGULATORY HUB (SPRINT-21)
// Modules 1-5: Complaints, File Compliance, Calendar, ACC Checklist, CDD/TFN
// Modules 6-10: Credit Representatives, Insurance, Memberships, Certificates,
//               Compliance Documents, Security Audits, Offshore Staff
// ============================================================================

// ============================================================================
// ENUMS — Module 1: Complaints
// ============================================================================

export const complaintTypeEnum = pgEnum("complaint_type_enum", [
  "standard",
  "hardship",
  "default_notice",
]);

export const complaintCategoryEnum = pgEnum("complaint_category_enum", [
  "service",
  "product",
  "fees",
  "communication",
  "advice",
  "privacy",
  "other",
]);

export const complaintStatusEnum = pgEnum("complaint_status_enum", [
  "open",
  "acknowledged",
  "investigating",
  "resolved",
  "escalated",
]);

export const complaintOutcomeEnum = pgEnum("complaint_outcome_enum", [
  "upheld",
  "partially_upheld",
  "rejected",
  "withdrawn",
]);

export const complaintNoteTypeEnum = pgEnum("complaint_note_type_enum", [
  "internal",
  "communication",
  "investigation",
]);

export const afcaStatusEnum = pgEnum("afca_status_enum", [
  "open",
  "responded",
  "determined",
  "closed",
]);

export const afcaOutcomeEnum = pgEnum("afca_outcome_enum", [
  "in_favour_firm",
  "in_favour_consumer",
  "withdrawn",
  "discontinued",
]);

export const afcaComplianceEnum = pgEnum("afca_compliance_enum", [
  "pending",
  "complied",
  "appealed",
]);

export const breachTypeEnum = pgEnum("breach_type_enum", [
  "disclosure",
  "conduct",
  "credit_code",
  "aps",
  "privacy",
  "aml_ctf",
  "other",
]);

export const breachSignificanceEnum = pgEnum("breach_significance_enum", [
  "minor",
  "moderate",
  "significant",
  "severe",
]);

export const breachStatusEnum = pgEnum("breach_status_enum", [
  "identified",
  "investigating",
  "remediated",
  "reported",
  "closed",
]);

// ============================================================================
// ENUMS — Module 2: File Compliance
// ============================================================================

export const complianceChecklistEnum = pgEnum("compliance_checklist_enum", [
  "pre_submission",
  "post_settlement",
  "annual_review",
]);

export const fileComplianceEnum = pgEnum("file_compliance_enum", [
  "pending",
  "passed",
  "failed",
  "remediated",
]);

export const bidStatusEnum = pgEnum("bid_status_enum", [
  "pending",
  "provided",
  "acknowledged",
  "waived",
]);

// ============================================================================
// ENUMS — Module 3: Compliance Calendar
// ============================================================================

export const complianceEventEnum = pgEnum("compliance_event_enum", [
  "renewal",
  "lodgement",
  "audit",
  "training",
  "review",
  "other",
]);

export const calendarStatusEnum = pgEnum("calendar_status_enum", [
  "upcoming",
  "due",
  "overdue",
  "completed",
  "cancelled",
]);

// ============================================================================
// ENUMS — Module 4: ACC Checklist
// ============================================================================

export const accChecklistCategoryEnum = pgEnum("acc_checklist_category_enum", [
  "identity",
  "financial",
  "legal",
  "regulatory",
  "consent",
  "other",
]);

// ============================================================================
// ENUMS — Module 5: TFN Register + CDD
// ============================================================================

export const tfnEntityEnum = pgEnum("tfn_entity_enum", [
  "contact",
  "company",
  "trust",
]);

export const tfnStatusEnum = pgEnum("tfn_status_enum", [
  "pending",
  "verified",
  "expired",
  "revoked",
]);

export const cddStatusEnum = pgEnum("cdd_status_enum", [
  "pending",
  "in_progress",
  "completed",
  "expired",
  "failed",
]);

export const amlRiskEnum = pgEnum("aml_risk_enum", [
  "low",
  "medium",
  "high",
  "prohibited",
]);

// ============================================================================
// ENUMS — Module 6: Credit Representatives
// ============================================================================

export const crAppointmentEnum = pgEnum("cr_appointment_enum", [
  "full",
  "limited",
  "provisional",
]);

export const crStatusEnum = pgEnum("cr_status_enum", [
  "active",
  "suspended",
  "terminated",
  "pending",
]);

export const crComplianceStatusEnum = pgEnum("cr_compliance_status_enum", [
  "compliant",
  "non_compliant",
  "under_review",
  "remediation_required",
]);

export const crDocumentTypeEnum = pgEnum("cr_document_type_enum", [
  "appointment",
  "authority",
  "training",
  "compliance",
  "termination",
  "other",
]);

export const fileReviewTypeEnum = pgEnum("file_review_type_enum", [
  "spot_check",
  "random",
  "targeted",
  "complaint_triggered",
  "annual",
]);

export const reviewStatusEnum = pgEnum("review_status_enum", [
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
]);

export const fileReviewOutcomeEnum = pgEnum("file_review_outcome_enum", [
  "satisfactory",
  "minor_issues",
  "significant_issues",
  "fail",
]);

export const mentoringStatusEnum = pgEnum("mentoring_status_enum", [
  "active",
  "completed",
  "suspended",
  "cancelled",
]);

export const trainingTypeEnum = pgEnum("training_type_enum", [
  "cpd",
  "product",
  "compliance",
  "induction",
  "remedial",
  "other",
]);

export const trainingStatusEnum = pgEnum("training_status_enum", [
  "scheduled",
  "in_progress",
  "completed",
  "failed",
  "expired",
]);

// ============================================================================
// ENUMS — Module 7: Insurance
// ============================================================================

export const insuranceTypeEnum = pgEnum("insurance_type_enum", [
  "pi",
  "public_liability",
  "cyber",
  "management_liability",
  "other",
]);

export const policyStatusEnum = pgEnum("policy_status_enum", [
  "active",
  "expired",
  "cancelled",
  "pending_renewal",
]);

export const endorsementTypeEnum = pgEnum("endorsement_type_enum", [
  "addition",
  "deletion",
  "amendment",
]);

// ============================================================================
// ENUMS — Module 8: Memberships
// ============================================================================

export const membershipTypeEnum = pgEnum("membership_type_enum", [
  "industry",
  "professional",
  "aggregator",
  "other",
]);

export const membershipStatusEnum = pgEnum("membership_status_enum", [
  "active",
  "expired",
  "suspended",
  "cancelled",
]);

// ============================================================================
// ENUMS — Module 9: Certificates
// ============================================================================

export const certificateTypeEnum = pgEnum("certificate_type_enum", [
  "diploma",
  "cert_iv",
  "degree",
  "accreditation",
  "lender_accreditation",
  "other",
]);

export const certificateHolderEnum = pgEnum("certificate_holder_enum", [
  "organisation",
  "credit_representative",
  "staff",
]);

export const certificateStatusEnum = pgEnum("certificate_status_enum", [
  "current",
  "expired",
  "revoked",
  "pending_renewal",
]);

// ============================================================================
// ENUMS — Module 10: Compliance Documents, Security Audits
// ============================================================================

export const complianceDocCategoryEnum = pgEnum("compliance_doc_category_enum", [
  "policy",
  "procedure",
  "template",
  "register",
  "report",
  "certificate",
  "other",
]);

export const documentStatusEnum = pgEnum("document_status_enum", [
  "draft",
  "active",
  "superseded",
  "archived",
]);

export const securityAuditTypeEnum = pgEnum("security_audit_type_enum", [
  "internal",
  "external",
  "regulatory",
  "certification",
]);

export const auditStatusEnum = pgEnum("audit_status_enum", [
  "planned",
  "in_progress",
  "completed",
  "cancelled",
]);

// Shared compliance status (used across many tables)
export const complianceStatusEnum = pgEnum("compliance_status_enum", [
  "compliant",
  "non_compliant",
  "partially_compliant",
  "not_applicable",
]);

export const riskLevelEnum = pgEnum("risk_level_enum", [
  "critical",
  "high",
  "medium",
  "low",
  "informational",
]);

// ============================================================================
// MODULE 1: COMPLAINTS TABLES
// ============================================================================

export const complaints = pgTable("crm_x_complaints", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  complaintReference: varchar("complaint_reference", { length: 50 }).notNull(),
  complaintType: complaintTypeEnum("complaint_type").notNull(),
  complaintCategory: complaintCategoryEnum("complaint_category").notNull(),
  complaintStatus: complaintStatusEnum("complaint_status").notNull().default("open"),
  complainantType: varchar("complainant_type", { length: 50 }).notNull(),
  complainantEntityId: uuid("complainant_entity_id"),
  complainantName: varchar("complainant_name", { length: 255 }).notNull(),
  complainantEmail: varchar("complainant_email", { length: 255 }),
  complainantPhone: varchar("complainant_phone", { length: 50 }),
  dealId: uuid("deal_id"),
  creditRepresentativeId: uuid("credit_representative_id"),
  receivedDate: date("received_date").notNull(),
  acknowledgedDate: date("acknowledged_date"),
  dueDate: date("due_date").notNull(),
  resolvedDate: date("resolved_date"),
  outcome: complaintOutcomeEnum("outcome"),
  resolutionSummary: text("resolution_summary"),
  compensationAmount: numeric("compensation_amount", { precision: 12, scale: 2 }),
  idrResponseSent: boolean("idr_response_sent").default(false),
  idrResponseDate: date("idr_response_date"),
  escalatedToAfca: boolean("escalated_to_afca").default(false),
  afcaReference: varchar("afca_reference", { length: 50 }),
  rootCause: text("root_cause"),
  systemicIssue: boolean("systemic_issue").default(false),
  remediationActions: text("remediation_actions"),
  assignedBrokerId: uuid("assigned_broker_id"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
});

export const complaintNotes = pgTable("crm_x_complaint_notes", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  complaintId: uuid("complaint_id").notNull(),
  noteType: complaintNoteTypeEnum("note_type").notNull().default("internal"),
  noteContent: text("note_content").notNull(),
  communicationMethod: varchar("communication_method", { length: 50 }),
  communicationDirection: varchar("communication_direction", { length: 20 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const afcaReferrals = pgTable("crm_x_afca_referrals", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  complaintId: uuid("complaint_id").notNull(),
  afcaReference: varchar("afca_reference", { length: 50 }).notNull(),
  afcaStatus: afcaStatusEnum("afca_status").notNull().default("open"),
  referralDate: date("referral_date").notNull(),
  responseDueDate: date("response_due_date"),
  responseSubmittedDate: date("response_submitted_date"),
  determinationDate: date("determination_date"),
  afcaOutcome: afcaOutcomeEnum("afca_outcome"),
  determinationSummary: text("determination_summary"),
  compensationOrdered: numeric("compensation_ordered", { precision: 12, scale: 2 }),
  complianceStatus: afcaComplianceEnum("compliance_status"),
  complianceDate: date("compliance_date"),
  afcaDocuments: jsonb("afca_documents"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const breachRegister = pgTable("crm_x_breach_register", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  breachReference: varchar("breach_reference", { length: 50 }).notNull(),
  breachType: breachTypeEnum("breach_type").notNull(),
  breachSignificance: breachSignificanceEnum("breach_significance").notNull(),
  breachStatus: breachStatusEnum("breach_status").notNull().default("identified"),
  breachTitle: varchar("breach_title", { length: 255 }).notNull(),
  breachDescription: text("breach_description").notNull(),
  dealId: uuid("deal_id"),
  creditRepresentativeId: uuid("credit_representative_id"),
  complaintId: uuid("complaint_id"),
  identifiedDate: date("identified_date").notNull(),
  identifiedBy: uuid("identified_by"),
  discoveryMethod: varchar("discovery_method", { length: 100 }),
  assessmentDate: date("assessment_date"),
  assessedBy: uuid("assessed_by"),
  significanceRationale: text("significance_rationale"),
  reportableToAsic: boolean("reportable_to_asic").default(false),
  asicReportDueDate: date("asic_report_due_date"),
  asicReportedDate: date("asic_reported_date"),
  asicReference: varchar("asic_reference", { length: 50 }),
  remediationRequired: boolean("remediation_required").default(false),
  remediationPlan: text("remediation_plan"),
  remediationDueDate: date("remediation_due_date"),
  remediationCompletedDate: date("remediation_completed_date"),
  clientsAffected: integer("clients_affected"),
  financialImpact: numeric("financial_impact", { precision: 12, scale: 2 }),
  closedDate: date("closed_date"),
  closureNotes: text("closure_notes"),
  assignedBrokerId: uuid("assigned_broker_id"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
});

// ============================================================================
// MODULE 2: FILE COMPLIANCE
// ============================================================================

export const fileComplianceChecks = pgTable("crm_x_file_compliance_checks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").notNull(),
  checklistType: complianceChecklistEnum("checklist_type").notNull(),
  complianceStatus: fileComplianceEnum("compliance_status").notNull().default("pending"),
  bidApplies: boolean("bid_applies"),
  bidStatus: bidStatusEnum("bid_status"),
  bidRationale: text("bid_rationale"),
  bidSignedDate: date("bid_signed_date"),
  bidSignoffName: varchar("bid_signoff_name", { length: 255 }),
  bidSignoffLicenceNumber: varchar("bid_signoff_licence_number", { length: 100 }),
  bidSignoffUserId: uuid("bid_signoff_user_id"),
  bidSignedOffAt: timestamp("bid_signed_off_at", { withTimezone: true }),
  rlAssessmentCompleted: boolean("rl_assessment_completed").default(false),
  rlNotUnsuitable: boolean("rl_not_unsuitable"),
  rlRationale: text("rl_rationale"),
  creditGuideProvided: boolean("credit_guide_provided").default(false),
  creditGuideDate: date("credit_guide_date"),
  creditProposalProvided: boolean("credit_proposal_provided").default(false),
  creditProposalDate: date("credit_proposal_date"),
  checklistItems: jsonb("checklist_items"),
  checkedBy: uuid("checked_by"),
  checkedDate: date("checked_date"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

/**
 * Compatibility projection for non-TFN document PII controls.
 *
 * `contains_tfn` stays in the table and read contract for old consumers, but
 * the database invariant fixes it to false. TFN detection and redaction are
 * machine-owned by the standard write adaptor.
 */
export const documentSecurityFlags = pgTable(
  "crm_x_document_security_flags",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    documentId: uuid("document_id").notNull(),
    containsTfn: boolean("contains_tfn").notNull().default(false),
    containsPii: boolean("contains_pii").notNull().default(false),
    piiTypes: text("pii_types").array().default(sql`'{}'::text[]`),
    redactionRequired: boolean("redaction_required").notNull().default(false),
    redactionVerified: boolean("redaction_verified").notNull().default(false),
    redactionVerifiedBy: uuid("redaction_verified_by"),
    redactionVerifiedDate: timestamp("redaction_verified_date", { withTimezone: true }),
    flaggedBy: uuid("flagged_by"),
    flaggedAt: timestamp("flagged_at", { withTimezone: true }).defaultNow().notNull(),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    noTfn: check(
      "crm_x_document_security_flags_no_tfn",
      sql`${table.containsTfn} IS FALSE`,
    ),
    noTfnPiiTypes: check(
      "crm_x_document_security_flags_no_tfn_pii_types",
      sql`lower(regexp_replace(array_to_string(${table.piiTypes}, '|'), '[^a-zA-Z0-9|]+', '', 'g')) !~ '(^|[|])[^|]*(tfn|taxfile)[^|]*([|]|$)'`,
    ),
    verifiedPiiOnly: check(
      "crm_x_document_security_flags_verified_pii_only",
      sql`NOT ${table.redactionVerified} OR ${table.containsPii}`,
    ),
  }),
);

// ============================================================================
// MODULE 3: COMPLIANCE CALENDAR
// ============================================================================

export const complianceCalendar = pgTable("crm_x_compliance_calendar", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  eventType: complianceEventEnum("event_type").notNull(),
  eventTitle: varchar("event_title", { length: 255 }).notNull(),
  eventDescription: text("event_description"),
  dueDate: date("due_date").notNull(),
  reminderDate: date("reminder_date"),
  completedDate: date("completed_date"),
  calendarStatus: calendarStatusEnum("calendar_status").notNull().default("upcoming"),
  isRecurring: boolean("is_recurring").default(false),
  recurrencePattern: varchar("recurrence_pattern", { length: 50 }),
  nextOccurrenceDate: date("next_occurrence_date"),
  assignedTo: uuid("assigned_to"),
  relatedEntityType: varchar("related_entity_type", { length: 50 }),
  relatedEntityId: uuid("related_entity_id"),
  completionNotes: text("completion_notes"),
  evidenceDocuments: jsonb("evidence_documents"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 4: ACC CHECKLIST
// ============================================================================

export const accChecklistItems = pgTable("crm_x_acc_checklist_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  complianceYear: integer("compliance_year").notNull(),
  category: accChecklistCategoryEnum("category").notNull(),
  itemCode: varchar("item_code", { length: 50 }),
  itemDescription: text("item_description").notNull(),
  isCompliant: boolean("is_compliant"),
  complianceNotes: text("compliance_notes"),
  evidenceReference: varchar("evidence_reference", { length: 255 }),
  reviewedBy: uuid("reviewed_by"),
  reviewedDate: date("reviewed_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// MODULE 5: TFN REGISTER + CDD RECORDS
// ============================================================================

export const tfnRegister = pgTable("crm_x_tfn_register", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: tfnEntityEnum("entity_type").notNull(),
  entityId: uuid("entity_id").notNull(),
  entityName: varchar("entity_name", { length: 255 }).notNull(),
  tfnProvided: boolean("tfn_provided").default(false),
  tfnProvidedDate: date("tfn_provided_date"),
  tfnDocumentReference: varchar("tfn_document_reference", { length: 255 }),
  tfnStatus: tfnStatusEnum("tfn_status").notNull().default("pending"),
  destructionDueDate: date("destruction_due_date"),
  destructionDate: date("destruction_date"),
  destructionMethod: varchar("destruction_method", { length: 100 }),
  destructionConfirmedBy: uuid("destruction_confirmed_by"),
  dealId: uuid("deal_id"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const tfnAccessLog = pgTable("crm_x_tfn_access_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  tfnRegisterId: uuid("tfn_register_id").notNull(),
  accessedBy: uuid("accessed_by").notNull(),
  accessTimestamp: timestamp("access_timestamp", { withTimezone: true }).defaultNow().notNull(),
  accessReason: text("access_reason").notNull(),
  accessType: varchar("access_type", { length: 50 }).notNull(),
  ipAddress: varchar("ip_address", { length: 45 }),
  userAgent: text("user_agent"),
});

export const cddRecords = pgTable("crm_x_cdd_records", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType: varchar("entity_type", { length: 50 }).notNull(),
  entityId: uuid("entity_id").notNull(),
  entityName: varchar("entity_name", { length: 255 }).notNull(),
  cddStatus: cddStatusEnum("cdd_status").notNull().default("pending"),
  riskRating: amlRiskEnum("risk_rating"),
  verificationDate: date("verification_date"),
  verifiedBy: uuid("verified_by"),
  verificationMethod: varchar("verification_method", { length: 100 }),
  idVerified: boolean("id_verified").default(false),
  idDocuments: jsonb("id_documents"),
  uboVerified: boolean("ubo_verified").default(false),
  uboDocumentation: jsonb("ubo_documentation"),
  pepCheckCompleted: boolean("pep_check_completed").default(false),
  pepCheckDate: date("pep_check_date"),
  pepIdentified: boolean("pep_identified").default(false),
  sanctionsCheckCompleted: boolean("sanctions_check_completed").default(false),
  sanctionsCheckDate: date("sanctions_check_date"),
  sanctionsMatch: boolean("sanctions_match").default(false),
  eddRequired: boolean("edd_required").default(false),
  eddCompleted: boolean("edd_completed").default(false),
  eddNotes: text("edd_notes"),
  nextReviewDate: date("next_review_date"),
  lastReviewDate: date("last_review_date"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 6: CREDIT REPRESENTATIVES
// ============================================================================

export const creditRepresentatives = pgTable("crm_x_credit_representatives", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  crNumber: varchar("cr_number", { length: 20 }).notNull(),
  appointmentType: crAppointmentEnum("appointment_type").notNull(),
  entityType: varchar("entity_type", { length: 20 }).notNull(),
  entityName: varchar("entity_name", { length: 255 }).notNull(),
  abn: varchar("abn", { length: 11 }),
  acn: varchar("acn", { length: 9 }),
  contactId: uuid("contact_id"),
  crStatus: crStatusEnum("cr_status").notNull().default("pending"),
  complianceStatus: crComplianceStatusEnum("compliance_status").notNull().default("under_review"),
  appointmentDate: date("appointment_date").notNull(),
  cessationDate: date("cessation_date"),
  authorisedCreditActivities: text("authorised_credit_activities").array(),
  conditions: text("conditions"),
  supervisorId: uuid("supervisor_id"),
  supervisionLevel: varchar("supervision_level", { length: 50 }),
  piPolicyReference: varchar("pi_policy_reference", { length: 100 }),
  piExpiryDate: date("pi_expiry_date"),
  cpdYearStart: date("cpd_year_start"),
  cpdHoursRequired: integer("cpd_hours_required"),
  cpdHoursCompleted: integer("cpd_hours_completed"),
  assignedBrokerId: uuid("assigned_broker_id"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
});

export const crDocuments = pgTable("crm_x_cr_documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  creditRepresentativeId: uuid("credit_representative_id").notNull(),
  documentType: crDocumentTypeEnum("document_type").notNull(),
  documentName: varchar("document_name", { length: 255 }).notNull(),
  documentStatus: documentStatusEnum("document_status").notNull().default("draft"),
  filePath: varchar("file_path", { length: 500 }),
  fileUrl: varchar("file_url", { length: 500 }),
  effectiveDate: date("effective_date"),
  expiryDate: date("expiry_date"),
  versionNumber: integer("version_number").default(1),
  supersedesId: uuid("supersedes_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const crFileReviews = pgTable("crm_x_cr_file_reviews", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  creditRepresentativeId: uuid("credit_representative_id").notNull(),
  dealId: uuid("deal_id"),
  reviewType: fileReviewTypeEnum("review_type").notNull(),
  reviewStatus: reviewStatusEnum("review_status").notNull().default("scheduled"),
  scheduledDate: date("scheduled_date").notNull(),
  completedDate: date("completed_date"),
  reviewerId: uuid("reviewer_id"),
  reviewOutcome: fileReviewOutcomeEnum("review_outcome"),
  findings: text("findings"),
  recommendations: text("recommendations"),
  remediationRequired: boolean("remediation_required").default(false),
  remediationDueDate: date("remediation_due_date"),
  remediationCompletedDate: date("remediation_completed_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const crMentoringPlans = pgTable("crm_x_cr_mentoring_plans", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  creditRepresentativeId: uuid("credit_representative_id").notNull(),
  planName: varchar("plan_name", { length: 255 }).notNull(),
  mentoringStatus: mentoringStatusEnum("mentoring_status").notNull().default("active"),
  startDate: date("start_date").notNull(),
  endDate: date("end_date"),
  mentorId: uuid("mentor_id"),
  mentorName: varchar("mentor_name", { length: 255 }),
  objectives: text("objectives"),
  milestones: jsonb("milestones").default(sql`'[]'::jsonb`),
  progressNotes: text("progress_notes"),
  completionDate: date("completion_date"),
  completionNotes: text("completion_notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const crTrainingRecords = pgTable("crm_x_cr_training_records", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  creditRepresentativeId: uuid("credit_representative_id").notNull(),
  trainingType: trainingTypeEnum("training_type").notNull(),
  trainingName: varchar("training_name", { length: 255 }).notNull(),
  trainingProvider: varchar("training_provider", { length: 255 }),
  trainingStatus: trainingStatusEnum("training_status").notNull().default("scheduled"),
  scheduledDate: date("scheduled_date"),
  completionDate: date("completion_date"),
  expiryDate: date("expiry_date"),
  cpdHours: numeric("cpd_hours", { precision: 5, scale: 1 }),
  cpdCategory: varchar("cpd_category", { length: 100 }),
  certificateReference: varchar("certificate_reference", { length: 100 }),
  certificateUrl: varchar("certificate_url", { length: 500 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 7: INSURANCE POLICIES
// ============================================================================

export const insurancePolicies = pgTable("crm_x_insurance_policies", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  insuranceType: insuranceTypeEnum("insurance_type").notNull(),
  policyNumber: varchar("policy_number", { length: 100 }).notNull(),
  policyName: varchar("policy_name", { length: 255 }).notNull(),
  insurerName: varchar("insurer_name", { length: 255 }).notNull(),
  brokerName: varchar("broker_name", { length: 255 }),
  coverAmount: numeric("cover_amount", { precision: 15, scale: 2 }),
  excessAmount: numeric("excess_amount", { precision: 12, scale: 2 }),
  coverageSummary: text("coverage_summary"),
  inceptionDate: date("inception_date").notNull(),
  expiryDate: date("expiry_date").notNull(),
  renewalReminderDate: date("renewal_reminder_date"),
  policyStatus: policyStatusEnum("policy_status").notNull().default("active"),
  premiumAmount: numeric("premium_amount", { precision: 12, scale: 2 }),
  premiumFrequency: varchar("premium_frequency", { length: 20 }),
  policyDocumentUrl: varchar("policy_document_url", { length: 500 }),
  certificateUrl: varchar("certificate_url", { length: 500 }),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const insuranceEndorsements = pgTable("crm_x_insurance_endorsements", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  insurancePolicyId: uuid("insurance_policy_id").notNull(),
  endorsementType: endorsementTypeEnum("endorsement_type").notNull(),
  endorsementNumber: varchar("endorsement_number", { length: 50 }),
  description: text("description").notNull(),
  effectiveDate: date("effective_date").notNull(),
  premiumAdjustment: numeric("premium_adjustment", { precision: 12, scale: 2 }),
  documentUrl: varchar("document_url", { length: 500 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 8: MEMBERSHIPS
// ============================================================================

export const memberships = pgTable("crm_x_memberships", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  membershipType: membershipTypeEnum("membership_type").notNull(),
  organisationName: varchar("organisation_name", { length: 255 }).notNull(),
  membershipNumber: varchar("membership_number", { length: 100 }),
  membershipStatus: membershipStatusEnum("membership_status").notNull().default("active"),
  joinDate: date("join_date"),
  expiryDate: date("expiry_date"),
  renewalDate: date("renewal_date"),
  annualFee: numeric("annual_fee", { precision: 10, scale: 2 }),
  benefitsSummary: text("benefits_summary"),
  contactName: varchar("contact_name", { length: 255 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  contactPhone: varchar("contact_phone", { length: 50 }),
  certificateUrl: varchar("certificate_url", { length: 500 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 9: CERTIFICATES
// ============================================================================

export const certificates = pgTable("crm_x_certificates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  certificateType: certificateTypeEnum("certificate_type").notNull(),
  certificateName: varchar("certificate_name", { length: 255 }).notNull(),
  certificateNumber: varchar("certificate_number", { length: 100 }),
  holderType: certificateHolderEnum("holder_type").notNull(),
  holderId: uuid("holder_id"),
  holderName: varchar("holder_name", { length: 255 }),
  issuingBody: varchar("issuing_body", { length: 255 }).notNull(),
  issueDate: date("issue_date").notNull(),
  expiryDate: date("expiry_date"),
  certificateStatus: certificateStatusEnum("certificate_status").notNull().default("current"),
  certificateUrl: varchar("certificate_url", { length: 500 }),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 10: COMPLIANCE DOCUMENTS
// ============================================================================

export const complianceDocuments = pgTable("crm_x_compliance_documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  documentCategory: complianceDocCategoryEnum("document_category").notNull(),
  documentName: varchar("document_name", { length: 255 }).notNull(),
  documentDescription: text("document_description"),
  version: varchar("version", { length: 20 }),
  effectiveDate: date("effective_date"),
  reviewDate: date("review_date"),
  documentStatus: documentStatusEnum("document_status").notNull().default("draft"),
  fileUrl: varchar("file_url", { length: 500 }),
  filePath: varchar("file_path", { length: 500 }),
  approvedBy: uuid("approved_by"),
  approvedDate: date("approved_date"),
  regulatoryReference: text("regulatory_reference"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// MODULE 10: SECURITY AUDITS
// ============================================================================

export const securityAudits = pgTable("crm_x_security_audits", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  auditType: securityAuditTypeEnum("audit_type").notNull(),
  auditName: varchar("audit_name", { length: 255 }).notNull(),
  auditDescription: text("audit_description"),
  auditScope: text("audit_scope"),
  systemsCovered: text("systems_covered").array(),
  auditStatus: auditStatusEnum("audit_status").notNull().default("planned"),
  plannedDate: date("planned_date"),
  startDate: date("start_date"),
  completionDate: date("completion_date"),
  auditorName: varchar("auditor_name", { length: 255 }),
  auditorOrganisation: varchar("auditor_organisation", { length: 255 }),
  overallRating: complianceStatusEnum("overall_rating"),
  executiveSummary: text("executive_summary"),
  reportUrl: varchar("report_url", { length: 500 }),
  nextAuditDate: date("next_audit_date"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const securityAuditItems = pgTable("crm_x_security_audit_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityAuditId: uuid("security_audit_id").notNull(),
  itemCode: varchar("item_code", { length: 50 }),
  itemCategory: varchar("item_category", { length: 100 }).notNull(),
  itemDescription: text("item_description").notNull(),
  complianceStatus: complianceStatusEnum("compliance_status").notNull().default("not_applicable"),
  riskLevel: riskLevelEnum("risk_level"),
  findingDescription: text("finding_description"),
  evidence: text("evidence"),
  remediationRequired: boolean("remediation_required").default(false),
  remediationAction: text("remediation_action"),
  remediationOwner: uuid("remediation_owner"),
  remediationDueDate: date("remediation_due_date"),
  remediationCompletedDate: date("remediation_completed_date"),
  sortOrder: integer("sort_order"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const securityAuditTemplates = pgTable("crm_x_security_audit_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  templateName: varchar("template_name", { length: 255 }).notNull(),
  templateDescription: text("template_description"),
  auditType: securityAuditTypeEnum("audit_type").notNull(),
  templateItems: jsonb("template_items").notNull().default(sql`'[]'::jsonb`),
  isActive: boolean("is_active").default(true),
  version: varchar("version", { length: 20 }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// OFFSHORE STAFF COMPLIANCE
// ============================================================================

export const offshoreStaffCompliance = pgTable("crm_x_offshore_staff_compliance", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id"),
  staffName: varchar("staff_name", { length: 255 }).notNull(),
  staffRole: varchar("staff_role", { length: 100 }),
  staffEmail: varchar("staff_email", { length: 255 }),
  providerCompany: varchar("provider_company", { length: 255 }).notNull(),
  providerCountry: varchar("provider_country", { length: 100 }).notNull(),
  accessLevel: varchar("access_level", { length: 50 }).notNull(),
  systemsAccessed: text("systems_accessed").array(),
  dataCategoriesAccessed: text("data_categories_accessed").array(),
  complianceStatus: complianceStatusEnum("compliance_status").notNull().default("compliant"),
  confidentialitySigned: boolean("confidentiality_signed").default(false),
  confidentialityDate: date("confidentiality_date"),
  privacyTrainingCompleted: boolean("privacy_training_completed").default(false),
  privacyTrainingDate: date("privacy_training_date"),
  startDate: date("start_date").notNull(),
  endDate: date("end_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// COMPLIANCE ESCALATION RULES (Sprint 28 F014)
// ============================================================================

export const escalationActionEnum = pgEnum("crm_x_escalation_action_enum", [
  "notify",
  "notify_and_flag",
  "auto_reassign",
  "block_progression",
]);

export const complianceEscalationRules = pgTable("crm_x_compliance_escalation_rules", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  calendarEventType: varchar("calendar_event_type", { length: 50 }).notNull(),
  daysBeforeDeadline: integer("days_before_deadline").notNull(),
  escalationAction: escalationActionEnum("escalation_action").notNull().default("notify"),
  notifyRoles: text("notify_roles").array().notNull().default(sql`'{}'`),
  escalationLabel: varchar("escalation_label", { length: 100 }).notNull(),
  isActive: boolean("is_active").notNull().default(true),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS — Module 1-5 ONLY (per spec AC004)
// Modules 6-10 are dormant until Sprint 22
// ============================================================================

// Complaints
export const insertComplaintSchema = createInsertSchema(complaints, {
  receivedDate: z.string(),
  acknowledgedDate: z.string().optional(),
  dueDate: z.string().optional(),
  resolvedDate: z.string().optional(),
  idrResponseDate: z.string().optional(),
  compensationAmount: z.string().optional(),
});
export const updateComplaintSchema = insertComplaintSchema.partial();
export type InsertComplaint = z.infer<typeof insertComplaintSchema>;
export type SelectComplaint = typeof complaints.$inferSelect;

// Complaint Notes
export const insertComplaintNoteSchema = createInsertSchema(complaintNotes);
export const updateComplaintNoteSchema = insertComplaintNoteSchema.partial();
export type InsertComplaintNote = z.infer<typeof insertComplaintNoteSchema>;
export type SelectComplaintNote = typeof complaintNotes.$inferSelect;

// AFCA Referrals
export const insertAfcaReferralSchema = createInsertSchema(afcaReferrals, {
  referralDate: z.string(),
  responseDueDate: z.string().optional(),
  responseSubmittedDate: z.string().optional(),
  determinationDate: z.string().optional(),
  complianceDate: z.string().optional(),
  compensationOrdered: z.string().optional(),
});
export const updateAfcaReferralSchema = insertAfcaReferralSchema.partial();
export type InsertAfcaReferral = z.infer<typeof insertAfcaReferralSchema>;
export type SelectAfcaReferral = typeof afcaReferrals.$inferSelect;

// Breach Register
export const insertBreachRegisterSchema = createInsertSchema(breachRegister, {
  identifiedDate: z.string(),
  assessmentDate: z.string().optional(),
  asicReportDueDate: z.string().optional(),
  asicReportedDate: z.string().optional(),
  remediationDueDate: z.string().optional(),
  remediationCompletedDate: z.string().optional(),
  closedDate: z.string().optional(),
  financialImpact: z.string().optional(),
});
export const updateBreachRegisterSchema = insertBreachRegisterSchema.partial();
export type InsertBreachRegister = z.infer<typeof insertBreachRegisterSchema>;
export type SelectBreachRegister = typeof breachRegister.$inferSelect;

// File Compliance Checks
export const insertFileComplianceCheckSchema = createInsertSchema(fileComplianceChecks, {
  bidSignedDate: z.string().optional(),
  // offset: true accepts ISO 8601 with a timezone offset (e.g. +10:00 AEST),
  // not just trailing-Z UTC. Without it valid Brisbane-local timestamps 400.
  bidSignedOffAt: z.string().datetime({ offset: true }).optional(),
  creditGuideDate: z.string().optional(),
  creditProposalDate: z.string().optional(),
  checkedDate: z.string().optional(),
});
export const updateFileComplianceCheckSchema = insertFileComplianceCheckSchema.partial();
export type InsertFileComplianceCheck = z.infer<typeof insertFileComplianceCheckSchema>;
export type SelectFileComplianceCheck = typeof fileComplianceChecks.$inferSelect;

// Document Security Flags — non-TFN PII only. containsTfn is read-only compatibility state.
const nonTfnPiiTypeSchema = z.string().refine(
  (value) => !/(tfn|taxfile)/.test(value.toLowerCase().replace(/[^a-z0-9]+/g, "")),
  { message: "piiTypes must not contain TFN labels" },
);

export const insertDocumentSecurityFlagSchema = z.object({
  documentId: z.string().uuid().optional(),
  containsPii: z.boolean().optional().default(false),
  piiTypes: z.array(nonTfnPiiTypeSchema).optional().default([]),
  redactionRequired: z.boolean().optional().default(false),
  flaggedBy: z.string().uuid().optional(),
  notes: z.string().optional(),
});
export const updateDocumentSecurityFlagSchema = z.object({
  containsPii: z.boolean().optional(),
  piiTypes: z.array(nonTfnPiiTypeSchema).optional(),
  redactionRequired: z.boolean().optional(),
  redactionVerified: z.boolean().optional(),
  redactionVerifiedBy: z.string().uuid().optional(),
  redactionVerifiedDate: z.string().datetime().optional(),
  notes: z.string().optional(),
});
export type InsertDocumentSecurityFlag = z.infer<typeof insertDocumentSecurityFlagSchema>;
export type UpdateDocumentSecurityFlag = z.infer<typeof updateDocumentSecurityFlagSchema>;
export type SelectDocumentSecurityFlag = typeof documentSecurityFlags.$inferSelect;

// Compliance Calendar
export const insertComplianceCalendarSchema = createInsertSchema(complianceCalendar, {
  dueDate: z.string(),
  reminderDate: z.string().optional(),
  completedDate: z.string().optional(),
  nextOccurrenceDate: z.string().optional(),
});
export const updateComplianceCalendarSchema = insertComplianceCalendarSchema.partial();
export type InsertComplianceCalendar = z.infer<typeof insertComplianceCalendarSchema>;
export type SelectComplianceCalendar = typeof complianceCalendar.$inferSelect;

// ACC Checklist Items
export const insertAccChecklistItemSchema = createInsertSchema(accChecklistItems, {
  reviewedDate: z.string().optional(),
});
export const updateAccChecklistItemSchema = insertAccChecklistItemSchema.partial();
export type InsertAccChecklistItem = z.infer<typeof insertAccChecklistItemSchema>;
export type SelectAccChecklistItem = typeof accChecklistItems.$inferSelect;

// TFN Register
export const insertTfnRegisterSchema = createInsertSchema(tfnRegister, {
  tfnProvidedDate: z.string().optional(),
  destructionDueDate: z.string().optional(),
  destructionDate: z.string().optional(),
});
export const updateTfnRegisterSchema = insertTfnRegisterSchema.partial();
export type InsertTfnRegister = z.infer<typeof insertTfnRegisterSchema>;
export type SelectTfnRegister = typeof tfnRegister.$inferSelect;

// TFN Access Log
export const insertTfnAccessLogSchema = createInsertSchema(tfnAccessLog);
export type InsertTfnAccessLog = z.infer<typeof insertTfnAccessLogSchema>;
export type SelectTfnAccessLog = typeof tfnAccessLog.$inferSelect;

// CDD Records
export const insertCddRecordSchema = createInsertSchema(cddRecords, {
  verificationDate: z.string().optional(),
  pepCheckDate: z.string().optional(),
  sanctionsCheckDate: z.string().optional(),
  nextReviewDate: z.string().optional(),
  lastReviewDate: z.string().optional(),
});
export const updateCddRecordSchema = insertCddRecordSchema.partial();
export type InsertCddRecord = z.infer<typeof insertCddRecordSchema>;
export type SelectCddRecord = typeof cddRecords.$inferSelect;

// Compliance Escalation Rules (Sprint 28 F014)
export const insertEscalationRuleSchema = createInsertSchema(complianceEscalationRules, {
  daysBeforeDeadline: z.number().int(),
  notifyRoles: z.array(z.string()).optional(),
});
export const updateEscalationRuleSchema = insertEscalationRuleSchema.partial();
export type InsertEscalationRule = z.infer<typeof insertEscalationRuleSchema>;
export type SelectEscalationRule = typeof complianceEscalationRules.$inferSelect;

// ============================================================================
// SELECT SCHEMAS (for response validation)
// ============================================================================
export const selectEscalationRuleSchema = createSelectSchema(complianceEscalationRules);
export const selectComplaintSchema = createSelectSchema(complaints);
export const selectAfcaReferralSchema = createSelectSchema(afcaReferrals);
export const selectBreachRegisterSchema = createSelectSchema(breachRegister);
export const selectComplianceCalendarSchema = createSelectSchema(complianceCalendar);
export const selectAccChecklistItemSchema = createSelectSchema(accChecklistItems);
export const selectFileComplianceCheckSchema = createSelectSchema(fileComplianceChecks);
export const selectDocumentSecurityFlagSchema = createSelectSchema(documentSecurityFlags);
export const selectTfnRegisterSchema = createSelectSchema(tfnRegister);
export const selectCddRecordSchema = createSelectSchema(cddRecords);

// ============================================================================
// ZOD SCHEMAS — Modules 6-10 (Sprint 22)
// Credit Representatives, Insurance, Memberships, Certificates,
// Compliance Documents, Security Audits, Offshore Staff
// ============================================================================

// Credit Representatives (Module 6)
export const insertCreditRepresentativeSchema = createInsertSchema(creditRepresentatives, {
  appointmentDate: z.string().optional(),
  cessationDate: z.string().optional(),
  piExpiryDate: z.string().optional(),
  cpdYearStart: z.string().optional(),
});
export const updateCreditRepresentativeSchema = insertCreditRepresentativeSchema.partial();
export type InsertCreditRepresentative = z.infer<typeof insertCreditRepresentativeSchema>;
export type SelectCreditRepresentative = typeof creditRepresentatives.$inferSelect;

// CR Documents
export const insertCrDocumentSchema = createInsertSchema(crDocuments, {
  effectiveDate: z.string().optional(),
  expiryDate: z.string().optional(),
});
export const updateCrDocumentSchema = insertCrDocumentSchema.partial();
export type InsertCrDocument = z.infer<typeof insertCrDocumentSchema>;
export type SelectCrDocument = typeof crDocuments.$inferSelect;

// CR File Reviews
export const insertCrFileReviewSchema = createInsertSchema(crFileReviews, {
  scheduledDate: z.string(),
  completedDate: z.string().optional(),
  remediationDueDate: z.string().optional(),
  remediationCompletedDate: z.string().optional(),
});
export const updateCrFileReviewSchema = insertCrFileReviewSchema.partial();
export type InsertCrFileReview = z.infer<typeof insertCrFileReviewSchema>;
export type SelectCrFileReview = typeof crFileReviews.$inferSelect;

// CR Mentoring Plans
export const insertCrMentoringPlanSchema = createInsertSchema(crMentoringPlans, {
  startDate: z.string(),
  endDate: z.string().optional(),
  completionDate: z.string().optional(),
});
export const updateCrMentoringPlanSchema = insertCrMentoringPlanSchema.partial();
export type InsertCrMentoringPlan = z.infer<typeof insertCrMentoringPlanSchema>;
export type SelectCrMentoringPlan = typeof crMentoringPlans.$inferSelect;

// CR Training Records
export const insertCrTrainingRecordSchema = createInsertSchema(crTrainingRecords, {
  scheduledDate: z.string().optional(),
  completionDate: z.string().optional(),
  expiryDate: z.string().optional(),
  cpdHours: z.string().optional(),
});
export const updateCrTrainingRecordSchema = insertCrTrainingRecordSchema.partial();
export type InsertCrTrainingRecord = z.infer<typeof insertCrTrainingRecordSchema>;
export type SelectCrTrainingRecord = typeof crTrainingRecords.$inferSelect;

// Insurance Policies (Module 7)
export const insertInsurancePolicySchema = createInsertSchema(insurancePolicies, {
  inceptionDate: z.string().optional(),
  expiryDate: z.string().optional(),
  renewalReminderDate: z.string().optional(),
  coverAmount: z.string().optional(),
  excessAmount: z.string().optional(),
  premiumAmount: z.string().optional(),
});
export const updateInsurancePolicySchema = insertInsurancePolicySchema.partial();
export type InsertInsurancePolicy = z.infer<typeof insertInsurancePolicySchema>;
export type SelectInsurancePolicy = typeof insurancePolicies.$inferSelect;

// Insurance Endorsements
export const insertInsuranceEndorsementSchema = createInsertSchema(insuranceEndorsements, {
  effectiveDate: z.string(),
  premiumAdjustment: z.string().optional(),
});
export const updateInsuranceEndorsementSchema = insertInsuranceEndorsementSchema.partial();
export type InsertInsuranceEndorsement = z.infer<typeof insertInsuranceEndorsementSchema>;
export type SelectInsuranceEndorsement = typeof insuranceEndorsements.$inferSelect;

// Memberships (Module 8)
export const insertMembershipSchema = createInsertSchema(memberships, {
  joinDate: z.string().optional(),
  expiryDate: z.string().optional(),
  renewalDate: z.string().optional(),
  annualFee: z.string().optional(),
});
export const updateMembershipSchema = insertMembershipSchema.partial();
export type InsertMembership = z.infer<typeof insertMembershipSchema>;
export type SelectMembership = typeof memberships.$inferSelect;

// Certificates (Module 9)
export const insertCertificateSchema = createInsertSchema(certificates, {
  issueDate: z.string(),
  expiryDate: z.string().optional(),
});
export const updateCertificateSchema = insertCertificateSchema.partial();
export type InsertCertificate = z.infer<typeof insertCertificateSchema>;
export type SelectCertificate = typeof certificates.$inferSelect;

// Compliance Documents (Module 10)
export const insertComplianceDocumentSchema = createInsertSchema(complianceDocuments, {
  effectiveDate: z.string().optional(),
  reviewDate: z.string().optional(),
  approvedDate: z.string().optional(),
});
export const updateComplianceDocumentSchema = insertComplianceDocumentSchema.partial();
export type InsertComplianceDocument = z.infer<typeof insertComplianceDocumentSchema>;
export type SelectComplianceDocument = typeof complianceDocuments.$inferSelect;

// Security Audits
export const insertSecurityAuditSchema = createInsertSchema(securityAudits, {
  plannedDate: z.string().optional(),
  startDate: z.string().optional(),
  completionDate: z.string().optional(),
  nextAuditDate: z.string().optional(),
});
export const updateSecurityAuditSchema = insertSecurityAuditSchema.partial();
export type InsertSecurityAudit = z.infer<typeof insertSecurityAuditSchema>;
export type SelectSecurityAudit = typeof securityAudits.$inferSelect;

// Security Audit Items
export const insertSecurityAuditItemSchema = createInsertSchema(securityAuditItems, {
  remediationDueDate: z.string().optional(),
  remediationCompletedDate: z.string().optional(),
});
export const updateSecurityAuditItemSchema = insertSecurityAuditItemSchema.partial();
export type InsertSecurityAuditItem = z.infer<typeof insertSecurityAuditItemSchema>;
export type SelectSecurityAuditItem = typeof securityAuditItems.$inferSelect;

// Security Audit Templates
export const insertSecurityAuditTemplateSchema = createInsertSchema(securityAuditTemplates);
export const updateSecurityAuditTemplateSchema = insertSecurityAuditTemplateSchema.partial();
export type InsertSecurityAuditTemplate = z.infer<typeof insertSecurityAuditTemplateSchema>;
export type SelectSecurityAuditTemplate = typeof securityAuditTemplates.$inferSelect;

// Offshore Staff Compliance
export const insertOffshoreStaffComplianceSchema = createInsertSchema(offshoreStaffCompliance, {
  startDate: z.string(),
  endDate: z.string().optional(),
  confidentialityDate: z.string().optional(),
  privacyTrainingDate: z.string().optional(),
});
export const updateOffshoreStaffComplianceSchema = insertOffshoreStaffComplianceSchema.partial();
export type InsertOffshoreStaffCompliance = z.infer<typeof insertOffshoreStaffComplianceSchema>;
export type SelectOffshoreStaffCompliance = typeof offshoreStaffCompliance.$inferSelect;

// Select schemas for modules 6-10
export const selectCreditRepresentativeSchema = createSelectSchema(creditRepresentatives);
export const selectCrDocumentSchema = createSelectSchema(crDocuments);
export const selectCrFileReviewSchema = createSelectSchema(crFileReviews);
export const selectInsurancePolicySchema = createSelectSchema(insurancePolicies);
export const selectInsuranceEndorsementSchema = createSelectSchema(insuranceEndorsements);
export const selectMembershipSchema = createSelectSchema(memberships);
export const selectCertificateSchema = createSelectSchema(certificates);
export const selectComplianceDocumentSchema = createSelectSchema(complianceDocuments);
export const selectSecurityAuditSchema = createSelectSchema(securityAudits);
export const selectSecurityAuditItemSchema = createSelectSchema(securityAuditItems);
export const selectOffshoreStaffComplianceSchema = createSelectSchema(offshoreStaffCompliance);

// ============================================================================
// DOCUMENT REDACTION (Sprint 28 F024)
// ============================================================================

export const redactionRuleTypeEnum = pgEnum("crm_x_redaction_rule_type_enum", [
  "tfn",
  "medicare",
  "credit_card",
  "phone",
  "email",
  "bank_account",
  "custom",
]);

export const redactionChannelEnum = pgEnum("crm_x_redaction_channel_enum", [
  // Retain the historical embedding value for existing audit records; new writes
  // use REDACTION_CHANNELS, which excludes the retired channel (#2219).
  "crm_write", "agent_write", "email_ingest", "csv_import", "document_upload",
  "extraction_provider", "embedding", "pdf_export", "audit", "telemetry",
]);

export const redactionOutcomeEnum = pgEnum("crm_x_redaction_outcome_enum", [
  "redacted", "redacted_flagged", "masked", "quarantined", "blocked", "no_findings",
]);

export const documentRedactionRules = pgTable("crm_x_document_redaction_rules", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  ruleName: varchar("rule_name", { length: 100 }).notNull(),
  ruleType: redactionRuleTypeEnum("rule_type").notNull(),
  pattern: text("pattern").notNull(),
  replacementText: varchar("replacement_text", { length: 50 }).notNull().default("[REDACTED]"),
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  isSystemRule: boolean("is_system_rule").notNull().default(false),
  priority: integer("priority").notNull().default(0),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

export const documentRedactionLog = pgTable("crm_x_document_redaction_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: uuid("document_id"),
  documentType: varchar("document_type", { length: 50 }),
  redactionRuleId: uuid("redaction_rule_id"),
  ruleName: varchar("rule_name", { length: 100 }),
  matchCount: integer("match_count").notNull().default(0),
  redactedHash: varchar("redacted_hash", { length: 64 }),
  channel: redactionChannelEnum("channel").notNull(),
  outcome: redactionOutcomeEnum("outcome").notNull(),
  subjectType: varchar("subject_type", { length: 64 }),
  subjectReference: text("subject_reference"),
  highConfidenceCount: integer("high_confidence_count").notNull().default(0),
  mediumConfidenceCount: integer("medium_confidence_count").notNull().default(0),
  maskedCount: integer("masked_count").notNull().default(0),
  /**
   * D22 (spec 4.1a): CHECKSUM_ONLY findings suppressed because their containing maximal
   * post-normalisation digit run exceeded 9 digits. Digit-free count only. Removing it
   * revokes the discriminator (4.1a revocation trigger 4).
   */
  longRunSuppressionCount: integer("long_run_suppression_count").notNull().default(0),
  confidenceTiers: text("confidence_tiers").array().notNull().default(sql`'{}'::text[]`),
  firingRuleIds: text("firing_rule_ids").array().notNull().default(sql`'{}'::text[]`),
  reasonCode: varchar("reason_code", { length: 64 }),
  /**
   * TEXT, not uuid: Clerk principal ids are text (`user_3As9...`) and req.user.userId
   * carries the Clerk id for human callers. A uuid column raised 22P02 on every genuine
   * finding. Service-token callers write a UUID; both forms are valid here.
   */
  performedBy: text("performed_by"),
  performedAt: timestamp("performed_at", { withTimezone: true }).defaultNow().notNull(),
  organisationId: uuid("organisation_id").notNull(),
}, (table) => [
  check("crm_x_redaction_log_no_findings_check", sql`
    ${table.outcome} <> 'no_findings' OR (
      ${table.matchCount} = 0
      AND ${table.highConfidenceCount} = 0
      AND ${table.mediumConfidenceCount} = 0
      AND ${table.maskedCount} = 0
      AND ${table.longRunSuppressionCount} = 0
      AND cardinality(${table.confidenceTiers}) = 0
      AND cardinality(${table.firingRuleIds}) = 0
      AND ${table.reasonCode} IS NULL
      AND ${table.redactedHash} ~ '^[0-9a-f]{64}$'
    ) IS TRUE
  `),
]);

// Zod schemas — Document Redaction
export const insertDocumentRedactionRuleSchema = createInsertSchema(documentRedactionRules, {
  ruleName: z.string().min(1).max(100),
  pattern: z.string().min(1),
  replacementText: z.string().max(50).optional(),
  description: z.string().optional(),
  priority: z.number().int().optional(),
});
export const updateDocumentRedactionRuleSchema = insertDocumentRedactionRuleSchema.partial();
export type InsertDocumentRedactionRule = z.infer<typeof insertDocumentRedactionRuleSchema>;
export type SelectDocumentRedactionRule = typeof documentRedactionRules.$inferSelect;

export const insertDocumentRedactionLogSchema = createInsertSchema(documentRedactionLog);
export type InsertDocumentRedactionLog = z.infer<typeof insertDocumentRedactionLogSchema>;
export type SelectDocumentRedactionLog = typeof documentRedactionLog.$inferSelect;

export const selectDocumentRedactionRuleSchema = createSelectSchema(documentRedactionRules);
export const selectDocumentRedactionLogSchema = createSelectSchema(documentRedactionLog);
